import type { ComponentPropsWithRef, ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '@/shared/lib'
import { FieldOutline } from '@/shared/ui/field-outline'
import { FloatingLabel } from '@/shared/ui/floating-label'

type FloatingFieldProps = Omit<ComponentPropsWithRef<'input'>, 'placeholder' | 'size'> & {
  /** Doubles as the placeholder — Verdant fields have no external label. */
  label: string
  /** 1–3 words under the field: «Рабочий адрес», «Только цифры», «Свободен». */
  hint?: ReactNode
  tone?: 'normal' | 'error' | 'success'
  /**
   * `md` is the 44px form field. `sm` is the 32px toolbar field — a search box
   * next to filter chips — where the label degrades to a plain placeholder,
   * because there is no room for it to float.
   */
  size?: 'sm' | 'md'
  /** Sits inside the field and shifts the text; not the bordered `prefix` block. */
  icon?: ReactNode
  /** Identifiers, amounts and codes are typed in mono. */
  isMono?: boolean
  /** A fixed leading segment with its own surface, e.g. a currency. */
  prefix?: ReactNode
  className?: string
}

const TONES = {
  normal: {
    field: 'bg-surface-sunken text-foreground',
    hint: 'text-muted-foreground',
  },
  error: {
    field: 'bg-destructive/8 text-destructive-text',
    hint: 'text-destructive',
  },
  success: {
    field: 'bg-success/8 text-success-text',
    hint: 'text-success-text',
  },
} as const

/** Where the label — and so the notch — starts once an icon takes the left edge. */
const NOTCH_WITH_ICON = 32

/**
 * The Verdant field: 44px, radius 10, sunken, and the label *is* the
 * placeholder — 13.5px centred at rest, sliding to 11px onto the border on
 * focus or once the field has a value, accent-coloured while focused.
 *
 * The float is driven by `:placeholder-shown`, not by React state, so it works
 * uncontrolled, costs no re-render, and stays correct when react-hook-form
 * sets a value imperatively.
 */
export function FloatingField({
  label,
  hint,
  tone = 'normal',
  size = 'md',
  icon,
  isMono = false,
  prefix,
  className,
  id,
  disabled,
  ...props
}: FloatingFieldProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const hintId = `${fieldId}-hint`
  const palette = TONES[tone]
  const hasIcon = icon !== undefined

  /*
   * The 32px field has no room for a floating label, so the label becomes the
   * placeholder and the whole notched-outline apparatus drops away: a plain
   * border, and focus shows as a border colour with no ring.
   */
  if (size === 'sm') {
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div data-slot="field" className="relative">
          {hasIcon && (
            <span className="pointer-events-none absolute top-1/2 left-2.75 -translate-y-1/2 text-muted-foreground [&_svg]:size-icon-md">
              {icon}
            </span>
          )}
          <input
            id={fieldId}
            disabled={disabled}
            aria-invalid={tone === 'error' || undefined}
            aria-describedby={hint === undefined ? undefined : hintId}
            aria-label={label}
            placeholder={label}
            className={cn(
              'h-control-sm w-full rounded-lg border border-border-strong bg-surface-sunken pr-2.5 text-body transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring disabled:cursor-not-allowed disabled:border-border-disabled disabled:bg-surface-1 disabled:text-fg-disabled',
              hasIcon ? 'pl-8' : 'pl-2.5',
              isMono && 'font-mono',
            )}
            {...props}
          />
        </div>

        {hint !== undefined && (
          <div id={hintId} className={cn('text-caption', palette.hint)}>
            {hint}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div
        data-slot="field"
        className={cn(
          // No `overflow-hidden` here: the label sits on the top border and any
          // clipping cuts it in half. The prefix rounds its own corners instead.
          'group/field relative flex h-control-lg items-stretch rounded-lg transition-colors',
          palette.field,
          disabled === true && 'bg-surface-1 text-fg-disabled',
        )}
      >
        <FieldOutline
          label={label}
          tone={disabled === true ? 'disabled' : tone}
          notchInset={hasIcon ? NOTCH_WITH_ICON : undefined}
        />

        {prefix !== undefined && (
          <div className="flex items-center rounded-l-[11px] border-r border-border bg-secondary px-2.75 font-mono text-caption text-fg-secondary">
            {prefix}
          </div>
        )}

        <div className="relative flex-1">
          {hasIcon && (
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground [&_svg]:size-[15px]">
              {icon}
            </span>
          )}
          <input
            id={fieldId}
            disabled={disabled}
            aria-invalid={tone === 'error' || undefined}
            aria-describedby={hint === undefined ? undefined : hintId}
            /* A single space, not the label: `:placeholder-shown` is what tells
             * the label whether to sit centred or float. */
            placeholder=" "
            className={cn(
              'peer size-full bg-transparent pr-3 text-body outline-none placeholder:text-transparent',
              'disabled:cursor-not-allowed',
              hasIcon ? 'pl-8.5' : 'pl-3',
              isMono && 'font-mono',
            )}
            {...props}
          />
          <FloatingLabel
            htmlFor={fieldId}
            tone={disabled === true ? 'disabled' : tone === 'normal' ? 'normal' : tone}
            className={hasIcon ? 'left-8.5 peer-placeholder-shown:left-8.5' : undefined}
          >
            {label}
          </FloatingLabel>
        </div>
      </div>

      {hint !== undefined && (
        <div id={hintId} className={cn('text-caption', palette.hint)}>
          {hint}
        </div>
      )}
    </div>
  )
}
